import { seasonDispatchDates, formatDate, dispatchDaysFrom, toISODate } from '@theme/dispatch-date-math';
import { StandardEvents } from '@shopify/events';

/*
 * Month-grid dispatch picker for "Peonies by Dispatch Date".
 *
 * Every date comes from seasonDispatchDates() in dispatch-date-math.js, and
 * the value written to properties[Dispatch Date] is formatDate(date), because
 * fulfilment reads that string. This file only owns the grid's appearance and
 * the variety-preference column beside it.
 *
 * Fail-open rules:
 *   - the dispatch days, the first and last Dispatch Day, the dates with no
 *     dispatch and the order-by hour are typed once, under Theme settings >
 *     Peony — Opening hours; the section prints them from snippets/opening.liquid
 *   - a blank weekday list and a missing last Dispatch Day fall back to the
 *     math module's defaults, so the picker is never silently empty
 *   - the <select> stays in the DOM, required and form-associated, so a
 *     no-JS visitor cannot post an order with no dispatch date on it
 *   - the three lines this file writes are settings on the section; the
 *     wording that shipped is kept below and used when one arrives blank
 */

const THURSDAY = 4;
const WEEKDAY_HEADS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const FALLBACK_LINES = {
  chosen: 'Leaves the farm [date]. Order by midday [cutoff].',
  expected: 'Expected around [date]',
  expectedEmpty: 'Nothing confirmed for [date] yet. We pack what is at its best.',
};

/* Puts the dates into a line typed in the theme editor. A placeholder the
   line does not use is simply not there; one it misspells is left as typed,
   which shows the mistake instead of printing "undefined". */
function fillLine(line, values) {
  return line.replace(/\[(date|cutoff)\]/g, (match, key) => values[key] ?? match);
}

/* Monday-first column index for a JS day number. */
function columnFor(date) {
  return (date.getDay() + 6) % 7;
}

class DispatchCalendar extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('[data-dispatch-date-select]');
    this.grid = this.querySelector('[data-dispatch-grid]');
    this.chosenLine = this.querySelector('[data-dispatch-chosen]');
    this.thursdayNote = this.querySelector('[data-dispatch-thursday]');
    this.closedNote = this.querySelector('[data-dispatch-closed]');
    this.closedDaysNote = this.querySelector('.dispatch-calendar__closed-note');
    this.aside = this.querySelector('.dispatch-calendar__aside');
    this.buyArea = this.querySelector('[data-dispatch-buy]');
    this.varietyInput = this.querySelector('[data-dispatch-variety-input]');
    this.varietyList = this.querySelector('[data-dispatch-variety-list]');
    this.varietyIntro = this.querySelector('[data-dispatch-variety-intro]');
    this.petal = this.querySelector('template[data-dispatch-petal]');

    this.lines = {
      chosen: (this.dataset.chosenLine || '').trim() || FALLBACK_LINES.chosen,
      expected: (this.dataset.expectedLine || '').trim() || FALLBACK_LINES.expected,
      expectedEmpty: (this.dataset.expectedEmptyLine || '').trim() || FALLBACK_LINES.expectedEmpty,
    };

    // The note about a closed day. Without both the text and the element to
    // show it in, a closed day has nothing to open and stays a plain numeral.
    this.closedDayText = this.closedDaysNote ? (this.dataset.closedNote || '').trim() : '';
    this.openClosedDay = null;

    this.chosen = new Set();
    this.varieties = this.readVarieties();
    this.bindPriceSync();

    const days = dispatchDaysFrom(this.dataset.dispatchDays);

    // The order-by hour and the word for it ("midnight", "8pm"). Without them
    // the math module reads the hour from the page's timetable.
    const typedHour = Number(this.dataset.cutoffHour);
    const hour = typedHour > 0 && typedHour <= 24 ? typedHour : undefined;
    const cutoffWord = (this.dataset.cutoffLabel || '').trim() || 'midnight';

    let dates = [];
    try {
      dates = seasonDispatchDates(this.dataset.seasonEnd, this.dataset.seasonStart || null, days, hour);
    } catch (e) {
      dates = [];
    }

    // Days the farm does not dispatch (public holidays), from the opening
    // hours settings; parsed here so an excluded day can never be picked.
    const closed = new Set(
      (this.dataset.closedDates || '')
        .split(/[\s,]+/)
        .map((d) => d.trim())
        .filter(Boolean)
    );
    if (closed.size) dates = dates.filter(({ date }) => !closed.has(toISODate(date)));
    this.closedDates = closed;

    if (!dates.length) {
      this.renderClosed();
      return;
    }

    this.offered = new Map();
    dates.forEach(({ date, cutoff }) => {
      const iso = toISODate(date);
      this.offered.set(iso, {
        date,
        label: formatDate(date),
        cutoffDate: formatDate(cutoff),
        cutoffLabel: `${cutoffWord} ${formatDate(cutoff)}`,
      });
    });

    this.populateSelect();
    this.buildGrid();
    this.bindClosedDayNote();

    this.select.addEventListener('change', () => this.apply(this.select.selectedOptions?.[0]?.dataset.iso));
    this.grid.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-iso]');
      if (!button) return;
      this.apply(button.dataset.iso);
    });
    this.grid.addEventListener('keydown', (event) => this.onGridKeydown(event));
    this.grid.addEventListener('focusin', (event) => {
      const day = event.target instanceof Element ? event.target.closest('button.dispatch-calendar__day') : null;
      if (day) this.setTabStop(day);
    });
    this.grid.addEventListener('focusout', (event) => {
      if (event.relatedTarget instanceof Node && this.grid.contains(event.relatedTarget)) return;
      this.setTabStop(this.grid.querySelector('.dispatch-calendar__day--selected'));
    });

    if (this.varietyList) {
      this.varietyList.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-variety]');
        if (!button) return;
        this.toggleVariety(button.dataset.variety);
      });
    }

    this.apply(this.isoHeldByForm() || [...this.offered.keys()][0]);

    // A page brought back by the Back button keeps the form's day (Chrome
    // restores the select, and the back-forward cache keeps the whole page)
    // while the grid may show another. The form's value is what an order
    // carries, so the grid follows it; a value that is no longer offered
    // puts both back on the first open day.
    this.onPageShow = () => this.apply(this.isoHeldByForm() || [...this.offered.keys()][0]);
    window.addEventListener('pageshow', this.onPageShow);

    // Once a box is in the cart, its variety picks are let go, so the form no
    // longer shows the box just added as if it were still waiting to go in.
    // The day stays: a second box is most often for the same run.
    this.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      const update = /** @type {any} */ (event);
      if (update.action !== 'add' || !update.promise) return;
      update.promise
        .then((result) => {
          if (!result?.detail?.didError) this.clearVarieties();
        })
        .catch(() => {});
    });
  }

  clearVarieties() {
    if (!this.chosen.size || !this.varietyList) return;
    this.chosen.clear();
    this.varietyList.querySelectorAll('button[data-variety]').forEach((button) => {
      button.setAttribute('aria-pressed', 'false');
      button.classList.remove('dispatch-calendar__variety-button--selected');
    });
    this.writePreference();
  }

  /* The day the form holds, if it is one still on offer. */
  isoHeldByForm() {
    const iso = this.select?.selectedOptions?.[0]?.dataset.iso;
    return iso && this.offered?.has(iso) ? iso : null;
  }

  /* ---------------------------------------------------------------- price */

  /* Horizon only re-renders a price block whose block id matches the
     variant-picker's. Here they differ, so the price would sit on the first
     variant for ever. Read the server-rendered map and write the selected
     variant's price in ourselves. */
  bindPriceSync() {
    const script = this.querySelector('[data-dispatch-prices]');
    const buy = this.querySelector('[data-dispatch-buy]');
    if (!script || !buy) return;

    let prices;
    try {
      prices = JSON.parse(script.textContent);
    } catch (e) {
      return;
    }
    if (!prices || typeof prices !== 'object') return;

    const write = () => {
      const checked = buy.querySelector('input[type="radio"][data-variant-id]:checked');
      const price = checked && prices[checked.dataset.variantId];
      if (!price) return;
      buy.querySelectorAll('.price').forEach((el) => {
        el.textContent = price;
      });
    };

    // Written again after the picker's own morph, which replaces this markup.
    buy.addEventListener('change', () => {
      write();
      requestAnimationFrame(write);
      setTimeout(write, 400);
    });

    write();
  }

  readVarieties() {
    const script = this.querySelector('[data-dispatch-variety-windows]');
    if (!script) return [];
    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /* ---------------------------------------------------------------- dates */

  populateSelect() {
    if (!this.select) return;
    let monthKey = null;
    let group = null;

    this.offered.forEach((entry, iso) => {
      const key = iso.slice(0, 7);
      if (key !== monthKey) {
        group = document.createElement('optgroup');
        group.label = entry.date.toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' });
        this.select.appendChild(group);
        monthKey = key;
      }
      const option = document.createElement('option');
      option.value = entry.label;
      option.textContent = entry.label;
      option.dataset.iso = iso;
      group.appendChild(option);
    });

    this.select.classList.add('dispatch-calendar__select--replaced');
    // Replaced, it is a 1px box: a Tab stop there shows no focus, and an arrow
    // key on it changes the day unseen. It stays the form control, and a
    // screen reader still finds it by its label.
    this.select.tabIndex = -1;
  }

  buildGrid() {
    if (!this.grid) return;
    this.grid.textContent = '';

    const isos = [...this.offered.keys()];
    const first = this.offered.get(isos[0]).date;
    const last = this.offered.get(isos[isos.length - 1]).date;

    let cursor = new Date(first.getFullYear(), first.getMonth(), 1);
    const end = new Date(last.getFullYear(), last.getMonth(), 1);

    while (cursor <= end) {
      this.grid.appendChild(this.buildMonth(cursor));
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }

    this.grid.hidden = false;
  }

  buildMonth(monthStart) {
    const month = document.createElement('div');
    month.className = 'dispatch-calendar__month';

    const label = document.createElement('h3');
    label.className = 'dispatch-calendar__month-label';
    label.textContent = monthStart.toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' });
    month.appendChild(label);

    const heads = document.createElement('div');
    heads.className = 'dispatch-calendar__heads';
    WEEKDAY_HEADS.forEach((name) => {
      const head = document.createElement('span');
      head.className = 'dispatch-calendar__head pp-label';
      head.setAttribute('aria-hidden', 'true');
      head.textContent = name;
      heads.appendChild(head);
    });
    month.appendChild(heads);

    const days = document.createElement('div');
    days.className = 'dispatch-calendar__days';

    for (let i = 0; i < columnFor(monthStart); i += 1) {
      const pad = document.createElement('span');
      pad.className = 'dispatch-calendar__pad';
      pad.setAttribute('aria-hidden', 'true');
      days.appendChild(pad);
    }

    const daysInMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate();
    for (let dayNum = 1; dayNum <= daysInMonth; dayNum += 1) {
      const date = new Date(monthStart.getFullYear(), monthStart.getMonth(), dayNum);
      const iso = toISODate(date);
      const entry = this.offered.get(iso);

      if (entry) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'dispatch-calendar__day dispatch-calendar__day--open';
        button.dataset.iso = iso;
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('aria-label', `${entry.label}. Order by ${entry.cutoffLabel}.`);
        // The petal goes in first so the numeral sits on top of it.
        const petal = this.petal?.content.firstElementChild;
        if (petal) button.appendChild(petal.cloneNode(true));
        button.appendChild(this.dayNumber(dayNum));
        days.appendChild(button);
      } else if (this.closedDates?.has(iso) === true && this.closedDayText) {
        // A day the farm is closed can be reached but not chosen: no data-iso,
        // so neither the click handler nor apply() ever sees it. Its name is
        // the date alone; why it is closed is the note it points to.
        const closed = document.createElement('button');
        closed.type = 'button';
        closed.className = 'dispatch-calendar__day dispatch-calendar__day--closed dispatch-calendar__day--holiday';
        closed.dataset.dispatchClosedDay = iso;
        closed.setAttribute('aria-disabled', 'true');
        closed.setAttribute('aria-describedby', this.closedDaysNote.id);
        closed.setAttribute('aria-label', formatDate(date));
        closed.appendChild(this.dayNumber(dayNum));
        days.appendChild(closed);
      } else {
        const closed = document.createElement('span');
        const isHoliday = this.closedDates?.has(iso) === true;
        closed.className = `dispatch-calendar__day dispatch-calendar__day--closed${
          isHoliday ? ' dispatch-calendar__day--holiday' : ''
        }`;
        closed.appendChild(this.dayNumber(dayNum));
        // A plain non-dispatch day is noise to a screen reader; a day the farm
        // is closed is a fact worth hearing, so it keeps its name.
        if (isHoliday) {
          closed.setAttribute('aria-label', `${dayNum}. Closed, we do not dispatch this day.`);
        } else {
          closed.setAttribute('aria-hidden', 'true');
        }
        days.appendChild(closed);
      }
    }

    month.appendChild(days);
    return month;
  }

  dayNumber(dayNum) {
    const num = document.createElement('span');
    num.className = 'dispatch-calendar__day-num';
    num.textContent = String(dayNum);
    return num;
  }

  /* ---------------------------------------------------------- closed days */

  /* The note about a closed day opens on that day and nowhere else: on hover,
     on keyboard focus and on a tap. Escape, moving focus away or a tap
     anywhere else closes it. The pointer may travel from the day onto the
     note and read it there. */
  bindClosedDayNote() {
    const note = this.closedDaysNote;
    if (!note || !this.closedDayText || !this.grid) return;

    note.textContent = this.closedDayText;

    const dayFrom = (target) =>
      target instanceof Element ? target.closest('[data-dispatch-closed-day]') : null;
    const staysInside = (day, next) => next instanceof Node && (day.contains(next) || note.contains(next));

    this.grid.addEventListener('mouseover', (event) => {
      const day = dayFrom(event.target);
      if (day) this.showClosedDayNote(day);
    });
    this.grid.addEventListener('mouseout', (event) => {
      const day = dayFrom(event.target);
      if (!day || day === document.activeElement || staysInside(day, event.relatedTarget)) return;
      this.hideClosedDayNote();
    });
    note.addEventListener('mouseleave', (event) => {
      const day = this.openClosedDay;
      if (!day || day === document.activeElement || staysInside(day, event.relatedTarget)) return;
      this.hideClosedDayNote();
    });

    this.grid.addEventListener('focusin', (event) => {
      const day = dayFrom(event.target);
      if (day) this.showClosedDayNote(day);
    });
    this.grid.addEventListener('focusout', (event) => {
      if (dayFrom(event.target)) this.hideClosedDayNote();
    });

    // Safari does not focus a button on click or tap, so the tap is handled
    // on its own and not left to focusin.
    this.grid.addEventListener('click', (event) => {
      const day = dayFrom(event.target);
      if (day) this.showClosedDayNote(day);
    });

    // Bound to the document only while a note is open.
    this.onNoteKeydown = (event) => {
      if (event.key === 'Escape') this.hideClosedDayNote();
    };
    this.onNotePointerdown = (event) => {
      const day = this.openClosedDay;
      if (day && staysInside(day, event.target)) return;
      this.hideClosedDayNote();
    };
  }

  showClosedDayNote(day) {
    const note = this.closedDaysNote;
    if (!note) return;

    if (!this.openClosedDay) {
      document.addEventListener('keydown', this.onNoteKeydown);
      document.addEventListener('pointerdown', this.onNotePointerdown);
    }
    this.openClosedDay = day;
    note.hidden = false;

    // Under the day and centred on it, then pulled back inside the calendar's
    // own column. Offsets are from the note's containing block: this element,
    // or the calendar's column where the stylesheet holds that in view.
    const above = 'dispatch-calendar__closed-note--above';
    const host = (note.offsetParent ?? this).getBoundingClientRect();
    const column = this.grid.getBoundingClientRect();
    const cell = day.getBoundingClientRect();
    const width = note.offsetWidth;
    const centred = cell.left + cell.width / 2 - width / 2;
    const left = Math.max(column.left, Math.min(centred, column.right - width));

    note.classList.remove(above);
    note.style.left = `${left - host.left}px`;
    note.style.top = `${cell.bottom - host.top}px`;
    note.style.bottom = '';

    // Where the window ends before the note does, and there is more room over
    // the day than under it, the note opens above the day. The room over it
    // starts under the header and status bar, which the page's scroll padding
    // already measures.
    const windowEnd = document.documentElement.clientHeight;
    const chrome = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
    if (note.getBoundingClientRect().bottom > windowEnd && cell.top - chrome > windowEnd - cell.bottom) {
      note.classList.add(above);
      note.style.top = 'auto';
      note.style.bottom = `${host.bottom - cell.top}px`;
    }
  }

  hideClosedDayNote() {
    if (!this.openClosedDay) return;
    this.openClosedDay = null;
    document.removeEventListener('keydown', this.onNoteKeydown);
    document.removeEventListener('pointerdown', this.onNotePointerdown);
    if (this.closedDaysNote) this.closedDaysNote.hidden = true;
  }

  disconnectedCallback() {
    this.hideClosedDayNote();
    if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
  }

  /* The calendar is one Tab stop, not one for every day: the chosen day, or
     the day the arrows have reached while focus is inside. Tab then leaves
     for the varieties in one press, and comes back to the chosen day. */
  setTabStop(day) {
    if (!day) return;
    this.grid.querySelectorAll('button.dispatch-calendar__day').forEach((button) => {
      button.tabIndex = button === day ? 0 : -1;
    });
  }

  onGridKeydown(event) {
    const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    // A closed day with a note is a stop on the way, in date order, so the
    // arrows do not strand a keyboard user on it.
    const buttons = [...this.grid.querySelectorAll('button.dispatch-calendar__day')];
    const index = buttons.indexOf(document.activeElement);
    if (index === -1) return;

    const isoOf = (button) => button.dataset.iso || button.dataset.dispatchClosedDay;

    let next = index;
    if (event.key === 'ArrowLeft') next = Math.max(0, index - 1);
    if (event.key === 'ArrowRight') next = Math.min(buttons.length - 1, index + 1);
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = buttons.length - 1;
    // Up and down are the same weekday a week away, as the grid is drawn.
    // Where that day is not offered the focus stays put.
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      const [year, month, day] = isoOf(buttons[index]).split('-').map(Number);
      const week = toISODate(new Date(year, month - 1, day + (event.key === 'ArrowDown' ? 7 : -7)));
      const found = buttons.findIndex((button) => isoOf(button) === week);
      if (found !== -1) next = found;
    }

    event.preventDefault();
    buttons[next].focus();
  }

  apply(iso) {
    const entry = iso ? this.offered.get(iso) : null;
    if (!entry) return;

    this.chosenIso = iso;

    // The <select> is the form control; the grid is only its skin.
    if (this.select && this.select.value !== entry.label) this.select.value = entry.label;

    this.grid.querySelectorAll('button[data-iso]').forEach((button) => {
      const on = button.dataset.iso === iso;
      button.classList.toggle('dispatch-calendar__day--selected', on);
      button.setAttribute('aria-pressed', String(on));
    });
    this.setTabStop(this.grid.querySelector('.dispatch-calendar__day--selected'));

    if (this.chosenLine) {
      this.chosenLine.textContent = fillLine(this.lines.chosen, { date: entry.label, cutoff: entry.cutoffDate });
    }

    if (this.thursdayNote) {
      this.thursdayNote.hidden = entry.date.getDay() !== THURSDAY;
    }

    this.renderVarieties(iso, entry.label);
  }

  /* ----------------------------------------------------------- varieties */

  expectedOn(iso) {
    return this.varieties.filter((v) => v.start && v.end && iso >= v.start && iso <= v.end);
  }

  renderVarieties(iso, dateLabel) {
    if (!this.varietyList) return;

    const expected = this.expectedOn(iso);
    const expectedNames = new Set(expected.map((v) => v.title));

    // Drop any preference that is not expected on the newly chosen date, so we
    // never send packing a name that was never offered for that run.
    [...this.chosen].forEach((name) => {
      if (!expectedNames.has(name)) this.chosen.delete(name);
    });

    if (this.varietyIntro) {
      this.varietyIntro.textContent = fillLine(expected.length ? this.lines.expected : this.lines.expectedEmpty, {
        date: dateLabel,
      });
    }

    this.varietyList.textContent = '';

    expected.forEach((variety) => {
      const item = document.createElement('li');
      item.className = 'dispatch-calendar__variety';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dispatch-calendar__variety-button';
      button.dataset.variety = variety.title;
      button.setAttribute('aria-pressed', String(this.chosen.has(variety.title)));
      button.classList.toggle('dispatch-calendar__variety-button--selected', this.chosen.has(variety.title));

      if (variety.image) {
        const img = document.createElement('img');
        img.className = 'dispatch-calendar__variety-image';
        img.src = variety.image;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 200;
        img.height = 200;
        button.appendChild(img);
      } else {
        const blank = document.createElement('span');
        blank.className = 'dispatch-calendar__variety-image dispatch-calendar__variety-image--blank';
        blank.setAttribute('aria-hidden', 'true');
        button.appendChild(blank);
      }

      const name = document.createElement('span');
      name.className = 'dispatch-calendar__variety-name';
      name.textContent = variety.title;
      button.appendChild(name);

      item.appendChild(button);
      this.varietyList.appendChild(item);
    });

    this.writePreference();
  }

  toggleVariety(name) {
    if (this.chosen.has(name)) {
      this.chosen.delete(name);
    } else {
      this.chosen.add(name);
    }

    this.varietyList.querySelectorAll('button[data-variety]').forEach((button) => {
      const on = this.chosen.has(button.dataset.variety);
      button.setAttribute('aria-pressed', String(on));
      button.classList.toggle('dispatch-calendar__variety-button--selected', on);
    });

    this.writePreference();
  }

  writePreference() {
    if (!this.varietyInput) return;
    // Order follows the column, not click order, so the packing slip reads the
    // same way the customer saw it.
    const ordered = [...this.varietyList.querySelectorAll('button[data-variety]')]
      .map((button) => button.dataset.variety)
      .filter((name) => this.chosen.has(name));

    this.varietyInput.value = ordered.length ? ordered.join(', ') : 'Any available';
  }

  /* -------------------------------------------------------------- closed */

  renderClosed() {
    if (this.grid) this.grid.hidden = true;
    if (this.chosenLine) this.chosenLine.textContent = '';
    if (this.thursdayNote) this.thursdayNote.hidden = true;
    if (this.buyArea) this.buyArea.hidden = true;
    if (this.select) this.select.disabled = true;
    if (this.closedNote) this.closedNote.hidden = false;

    // Out of season the closed-day note and the varieties aside describe a
    // calendar that is not on the page.
    if (this.closedDaysNote) this.closedDaysNote.hidden = true;
    if (this.aside) this.aside.hidden = true;

    // A disabled select is skipped by validation as well as submission, so
    // required no longer blocks anything. Stop the form itself instead.
    if (this.buyArea) {
      this.buyArea
        .querySelectorAll('button[type="submit"], input[type="submit"], [name="add"]')
        .forEach((el) => {
          el.disabled = true;
        });
    }

    const field = this.querySelector('[data-dispatch-field]');
    if (field) field.hidden = true;
  }
}

if (!customElements.get('dispatch-calendar')) {
  customElements.define('dispatch-calendar', DispatchCalendar);
}
